import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Globe, Building2, FlaskConical, Smartphone, Tablet, Monitor,
  Languages, CheckCircle, AlertCircle, Loader2, ChevronDown, FileText
} from 'lucide-react';
import api from '../../services/api';

/**
 * Primary/Variant Assessment Creation Modal
 * Allows users to:
 * 1. Create Primary assessment (default US-English, Web)
 * 2. Create Variant (select primary, device, language)
 */
const PrimaryVariantModal = ({ 
  isOpen, 
  onClose, 
  onCreatePrimary, 
  onCreateVariant, 
  existingPrimaries = [],
  preselectedPrimary = null,
  preselectedDeviceId = null
}) => {
  const [mode, setMode] = useState(preselectedPrimary ? 'variant' : 'primary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState({ phone: [], tablet: [], desktop: [] });
  const [loadingDevices, setLoadingDevices] = useState(true);

  // Primary form state
  const [primaryForm, setPrimaryForm] = useState({
    name: '',
    description: '',
    library_type: 'sponsor',
    assessment_type: 'fixed',
    language: 'en-US',
    default_device_id: null
  });

  // Variant form state
  const [variantForm, setVariantForm] = useState({
    primary_assessment_id: preselectedPrimary?.id || '',
    primary_assessment_uuid: preselectedPrimary?.uuid || '',
    device_id: preselectedDeviceId || '',
    language: '',
    variant_name: '',
    copy_structure: true
  });

  // Fetch devices on mount
  useEffect(() => {
    if (isOpen) {
      fetchDevices();
    }
  }, [isOpen]);
  
  // Update form when preselected values change
  useEffect(() => {
    if (isOpen && preselectedPrimary) {
      setMode('variant');
      setVariantForm(prev => ({
        ...prev,
        primary_assessment_id: preselectedPrimary.id,
        primary_assessment_uuid: preselectedPrimary.uuid,
        device_id: preselectedDeviceId || prev.device_id
      }));
    }
  }, [isOpen, preselectedPrimary, preselectedDeviceId]);

  const fetchDevices = async () => {
    setLoadingDevices(true);
    try {
      const response = await api.get('/api/assessment-builder/devices/');
      const data = response.data;
      setDevices(data.grouped || { phone: [], tablet: [], desktop: [] });
      
      // Set default device to first Web Desktop for primaries
      const webDesktop = data.grouped.desktop?.find(d => d.name.includes('Web Desktop'));
      if (webDesktop) {
        setPrimaryForm(prev => ({ ...prev, default_device_id: webDesktop.id }));
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handlePrimarySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onCreatePrimary(primaryForm);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create primary assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onCreateVariant(variantForm);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create variant');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPrimaryForm({
      name: '',
      description: '',
      library_type: 'sponsor',
      assessment_type: 'fixed',
      language: 'en-US',
      default_device_id: null
    });
    setVariantForm({
      primary_assessment_id: '',
      primary_assessment_uuid: '',
      device_id: '',
      language: '',
      variant_name: '',
      copy_structure: true
    });
    setError('');
    setMode('primary');
    onClose();
  };

  if (!isOpen) return null;

  const libraryOptions = [
    { value: 'vpg', label: 'VPG Global Library', icon: Globe, color: 'text-blue-600' },
    { value: 'sponsor', label: 'Sponsor Library', icon: Building2, color: 'text-primary-600' },
    { value: 'study', label: 'Study-Specific Library', icon: FlaskConical, color: 'text-purple-600' }
  ];

  const assessmentTypes = [
    { value: 'fixed', label: 'Fixed Form' },
    { value: 'adaptive', label: 'Adaptive (CAT)' },
    { value: 'item_bank', label: 'Item Library' }
  ];

  const commonLanguages = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'es-ES', label: 'Spanish (Spain)' },
    { code: 'es-MX', label: 'Spanish (Mexico)' },
    { code: 'fr-FR', label: 'French (France)' },
    { code: 'de-DE', label: 'German (Germany)' },
    { code: 'it-IT', label: 'Italian (Italy)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ja-JP', label: 'Japanese (Japan)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'ko-KR', label: 'Korean (South Korea)' }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Create Assessment</h2>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 pt-4">
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setMode('primary')}
                className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all ${
                  mode === 'primary'
                    ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm dark:shadow-gray-900/20'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Create Primary
              </button>
              <button
                onClick={() => setMode('variant')}
                className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all ${
                  mode === 'variant'
                    ? 'bg-white dark:bg-gray-700 text-purple-600 shadow-sm dark:shadow-gray-900/20'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Languages className="w-4 h-4 inline mr-2" />
                Create Variant
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {mode === 'primary' ? (
              <form onSubmit={handlePrimarySubmit} className="space-y-6">
                {/* Assessment Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assessment Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={primaryForm.name}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, name: e.target.value })}
                    placeholder="e.g., PHQ-9 Depression Scale"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={primaryForm.description}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, description: e.target.value })}
                    placeholder="Brief description of this assessment..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Library Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Library Type *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {libraryOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPrimaryForm({ ...primaryForm, library_type: option.value })}
                          className={`p-4 border-2 rounded-lg transition-all ${
                            primaryForm.library_type === option.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`w-6 h-6 ${option.color} mx-auto mb-2`} />
                          <div className="text-sm font-medium text-gray-900">{option.label.split(' ')[0]}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Assessment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assessment Type *
                  </label>
                  <select
                    value={primaryForm.assessment_type}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, assessment_type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {assessmentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Language *
                  </label>
                  <select
                    value={primaryForm.language}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, language: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {commonLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Primary assessments typically use en-US</p>
                </div>

                {/* Default Device */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Device
                  </label>
                  <select
                    value={primaryForm.default_device_id || ''}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, default_device_id: parseInt(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select device...</option>
                    <optgroup label="Desktop">
                      {devices.desktop?.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name} ({device.screen_width}×{device.screen_height})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Typically Web Desktop for primary forms</p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !primaryForm.name}
                    className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Create Primary
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVariantSubmit} className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-purple-900">
                    <Languages className="w-4 h-4 inline mr-2" />
                    Variants copy the primary's structure and allow independent editing for specific languages and devices.
                  </p>
                </div>

                {/* Select Primary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Primary Assessment *
                  </label>
                  <select
                    required
                    value={variantForm.primary_assessment_id}
                    onChange={(e) => {
                      const selected = existingPrimaries.find(p => String(p.id) === e.target.value);
                      setVariantForm({ ...variantForm, primary_assessment_id: e.target.value, primary_assessment_uuid: selected?.uuid || '' });
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Choose a primary assessment...</option>
                    {existingPrimaries.map((primary) => (
                      <option key={primary.id} value={primary.id}>
                        {primary.name} (v{primary.version})
                      </option>
                    ))}
                  </select>
                  {existingPrimaries.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600">
                      No primary assessments available. Create a primary first.
                    </p>
                  )}
                </div>

                {/* Select Device */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Device *
                  </label>
                  {loadingDevices ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <select
                      required
                      value={variantForm.device_id}
                      onChange={(e) => setVariantForm({ ...variantForm, device_id: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select device...</option>
                      <optgroup label="📱 Phones">
                        {devices.phone?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="📱 Tablets">
                        {devices.tablet?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🖥️ Desktop">
                        {devices.desktop?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  )}
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language *
                  </label>
                  <select
                    required
                    value={variantForm.language}
                    onChange={(e) => setVariantForm({ ...variantForm, language: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select language...</option>
                    {commonLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Variant Name (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variant Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={variantForm.variant_name}
                    onChange={(e) => setVariantForm({ ...variantForm, variant_name: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If left blank, will auto-generate as: "{existingPrimaries.find(p => p.id == variantForm.primary_assessment_id)?.name || 'Primary'} - {variantForm.language} ({devices.phone?.concat(devices.tablet, devices.desktop)?.find(d => d.id == variantForm.device_id)?.name || 'Device'})"
                  </p>
                </div>

                {/* Copy Structure */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="copy_structure"
                    checked={variantForm.copy_structure}
                    onChange={(e) => setVariantForm({ ...variantForm, copy_structure: e.target.checked })}
                    className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="copy_structure" className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">Copy structure from primary</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Recommended: Copies all screens and questions. Uncheck to start with empty variant.
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !variantForm.primary_assessment_id || !variantForm.device_id || !variantForm.language}
                    className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Variant...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Create Variant
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PrimaryVariantModal;
